import React, { useEffect } from 'react'
import styled from 'styled-components'
import { CheckCircleTwoTone } from '@ant-design/icons'

// Replace with your actual image
import previewImage from '../../assets/images/stock-preview.png'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import GrowthEngineFeatureEmptyState from 'src/components/growthEngine/GrowthEngineFeatureEmptyState'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import useGrowthEngineFeatureGuidance from 'src/hooks/useGrowthEngineFeatureGuidance'
import {
  FREE_STOCK_APP_TRY_TITLE,
  FREE_STOCK_DESKTOP_LEAD,
  FREE_STOCK_UPGRADE_CTA,
  getFreeStockDashboardEmptyProps,
  getStockLimitPlanCopy,
} from 'src/utils/growthEngineStockHonestFirstTry'
import { notifyStockTakePaywallSales } from 'src/utils/stockTakePaywallNotify'

const Container = styled.div`
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 12px;
  background-color: #fff;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
`

const Subheading = styled.p`
  color: #888;
  font-size: 0.9rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
`

const TryFirst = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 12px;
  margin: 1.25rem 0 1.5rem;
  padding: 1.2rem 1.4rem 1.3rem;
  text-align: left;
`

const TryTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0 0 0.4rem;
`

const TryBody = styled.p`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.45;
  margin: 0 0 0.25rem;
`

const Card = styled.div`
  background-color: #f9f9f9;
  padding: 1.8rem;
  border-radius: 12px;
  margin-bottom: 2rem;
`

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`

const CardTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`

const Description = styled.p`
  color: #555;
`

const Benefits = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 1.5rem;
`

const BenefitItem = styled.li`
  display: flex;
  align-items: start;
  gap: 0.6rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #333;
`

const PreviewWrapper = styled.div`
  margin-top: 2rem;
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  max-height: 250px;
`

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  filter: blur(0.8px);
  object-fit: cover;
`

const PreviewOverlay = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: rgba(51, 51, 51, 0.9);
  color: white;
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border-radius: 0.5rem;
  z-index: 2;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`

const PrimaryButton = styled.button`
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: white;
  padding: 0.9rem 1.2rem;
  font-size: 1rem;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

const StockLimit = ({ copy = {} }) => {
  const venueId = useSelector(state => state.venue?.venue?._id)
  const currentPlan = useSelector(
    state => state.venue?.venue?.usageLimits?.currentPlan,
  )
  const history = useHistory()
  const guidedSuccess = useGrowthEngineFeatureGuidance({
    venueId,
    surface: 'stock_takes',
    featureId: 'stock_takes',
  })
  const guidedEmpty = getFreeStockDashboardEmptyProps(guidedSuccess, {
    currentPlan,
  })
  const planCopy = getStockLimitPlanCopy(currentPlan)
  const {
    title = 'Stock',
    subheading = FREE_STOCK_DESKTOP_LEAD,
    cardTitle = 'Full reports live here. Count and order to par on this list.',
    description = planCopy.description,
    benefits = [
      'Record accurate stock levels across any date range',
      'Get automated stock & wastage reports to spot inefficiencies',
      'Lock periods to protect your data and maintain reporting accuracy',
      'Track performance over time to drive better GP',
    ],
    previewOverlay = 'Desktop reporting preview',
    primaryCta = FREE_STOCK_UPGRADE_CTA,
  } = copy

  useEffect(() => {
    if (!venueId) return undefined
    notifyStockTakePaywallSales({
      venueId,
      source: 'client',
      platform: 'dashboard',
      stockTakeTitle: 'Desktop stock reporting paywall',
    })
    return undefined
  }, [venueId])

  return (
    <Container>
      <Title>{title}</Title>
      <Subheading data-testid='stock-desktop-lead'>{subheading}</Subheading>

      <TryFirst data-testid='stock-app-try-first'>
        <TryTitle>{FREE_STOCK_APP_TRY_TITLE}</TryTitle>
        <TryBody data-testid='stock-app-try-body'>{planCopy.tryBody}</TryBody>
        <GrowthEngineHumanDemoRow align='center' featureId='stock_takes' />
      </TryFirst>

      {guidedEmpty ? (
        <GrowthEngineFeatureEmptyState
          title={guidedEmpty.title}
          body={guidedEmpty.body}
          valueLine={guidedEmpty.valueLine}
          estimatedMinutes={guidedEmpty.estimatedMinutes}
          honestNote={guidedEmpty.honestNote}
          ctaLabel={guidedEmpty.ctaLabel}
          hideHumanPath
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{cardTitle}</CardTitle>
          <Description>{description}</Description>
        </CardHeader>

        <Benefits>
          {benefits.map(item => (
            <BenefitItem key={item}>
              <CheckCircleTwoTone twoToneColor='#52c41a' /> {item}
            </BenefitItem>
          ))}
        </Benefits>

        <PreviewWrapper>
          <PreviewImage src={previewImage} alt='Stock preview blurred' />
          <PreviewOverlay>{previewOverlay}</PreviewOverlay>
        </PreviewWrapper>
      </Card>

      <ButtonGroup>
        <PrimaryButton
          data-testid='stock-desktop-upgrade'
          onClick={() => history.push(`/v/${venueId}/subscriptions`)}
        >
          {primaryCta}
        </PrimaryButton>
      </ButtonGroup>
    </Container>
  )
}

export default StockLimit
